import React, { useState, useEffect } from 'react';
import { CreateAgentRequest, IntegrationTool, Agent, HandoffConfig } from '@/types';
import { agentsService } from '@/services/agents';
import { integrationsService } from '@/services/integrations';
import toast from 'react-hot-toast';

interface AgentCreationFormProps {
  onSubmit: (agent: Agent) => void;
  onCancel: () => void;
  isOpen: boolean;
  workspaceId: string;
  createAgent: (data: CreateAgentRequest) => Promise<Agent>; // Add createAgent prop
}

interface ToolCategory {
  name: string;
  tools: IntegrationTool[];
  icon: string;
  description: string;
}

const AgentCreationForm: React.FC<AgentCreationFormProps> = ({ onSubmit, onCancel, isOpen, workspaceId, createAgent }) => {
  const [formData, setFormData] = useState<Omit<CreateAgentRequest, 'workspace_id'>>({
    name: '',
    description: '',
    instructions: '',
    model: 'gpt-4o-mini',
    tools: [],
    handoffs: [],
    guardrails: {}, // Changed from [] to {} - should be an object
    model_settings: {
      temperature: 0.7,
      max_tokens: 1500
    }
  });

  const [availableTools, setAvailableTools] = useState<ToolCategory[]>([]);
  const [selectedTools, setSelectedTools] = useState<string[]>([]);
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [selectedHandoffs, setSelectedHandoffs] = useState<string[]>([]); // Changed to string[] to match the expected type
  const [isLoading, setIsLoading] = useState(false);
  const [showToolSelector, setShowToolSelector] = useState(false);
  const [showHandoffSelector, setShowHandoffSelector] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [handoffSearchTerm, setHandoffSearchTerm] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load available tools and agents on component mount
  useEffect(() => {
    if (isOpen && workspaceId) {
      loadAvailableTools();
      loadAvailableAgents();
    } else if (isOpen && !workspaceId) {
      console.warn('AgentCreationForm opened without workspace ID');
      toast.error('No workspace selected. Please select a workspace first.');
    }
  }, [isOpen, workspaceId]);

  const loadAvailableTools = async () => {
    try {
      const tools = await agentsService.getAvailableTools();
      
      // Group tools by integration type
      const grouped = tools.reduce((acc, tool) => {
        if (!acc[tool.integration_type]) {
          acc[tool.integration_type] = [];
        }
        acc[tool.integration_type].push(tool);
        return acc;
      }, {} as Record<string, IntegrationTool[]>);

      const categories: ToolCategory[] = Object.entries(grouped).map(([type, tools]) => {
        const categoryInfo = getCategoryInfo(type);
        return {
          name: categoryInfo.name,
          tools,
          icon: categoryInfo.icon,
          description: categoryInfo.description
        };
      });

      setAvailableTools(categories);
    } catch (error) {
      console.error('Error loading tools:', error);
      toast.error('Failed to load available tools');
    }
  };

  const loadAvailableAgents = async () => {
    try {
      if (!workspaceId) {
        console.warn('No workspace ID available for loading agents');
        return;
      }
      
      const response = await agentsService.getAgents({ 
        workspace_id: workspaceId, // Add the missing workspace_id parameter
        page: 1, 
        per_page: 100, // Changed from 'size' to 'per_page' to match the service interface
        is_active: true // Changed from 'status: active' to 'is_active: true'
      });
      
      // Filter out the current agent being created (can't handoff to itself)
      const agents = response.agents.filter(agent => 
        agent.workspace_id === workspaceId && agent.is_active
      );
      
      setAvailableAgents(agents);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Failed to load available agents');
    }
  };

  const getCategoryInfo = (type: string) => {
    const categoryMap: Record<string, { name: string; icon: string; description: string }> = {
      jira: {
        name: 'Jira',
        icon: '🎯',
        description: 'Issue tracking and project management'
      },
      github: {
        name: 'GitHub',
        icon: '🐙',
        description: 'Code repository and collaboration'
      },
      confluence: {
        name: 'Confluence',
        icon: '📚',
        description: 'Documentation and knowledge management'
      },
      slack: {
        name: 'Slack',
        icon: '💬',
        description: 'Team communication and messaging'
      }
    };

    return categoryMap[type] || {
      name: type.charAt(0).toUpperCase() + type.slice(1),
      icon: '🔧',
      description: 'Custom integration tools'
    };
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Agent name is required';
    } else if (formData.name.length < 3) {
      newErrors.name = 'Agent name must be at least 3 characters';
    }

    if (!formData.instructions.trim()) {
      newErrors.instructions = 'Instructions are required';
    } else if (formData.instructions.length < 20) {
      newErrors.instructions = 'Instructions must be at least 20 characters';
    }

    if (formData.model_settings?.temperature !== undefined && (formData.model_settings.temperature < 0 || formData.model_settings.temperature > 2)) {
      newErrors.temperature = 'Temperature must be between 0 and 2';
    }

    if (formData.model_settings?.max_tokens !== undefined && (formData.model_settings.max_tokens < 100 || formData.model_settings.max_tokens > 4000)) {
      newErrors.max_tokens = 'Max tokens must be between 100 and 4000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!workspaceId) {
      toast.error('Workspace ID is required');
      return;
    }

    setIsLoading(true);
    try {
      const agentData: CreateAgentRequest = {
        ...formData,
        workspace_id: workspaceId, // Add the missing workspace_id
        tools: selectedTools,
        handoffs: selectedHandoffs // Already a string[] array of agent names
      };

      const createdAgent = await createAgent(agentData);
      onSubmit(createdAgent);
      resetForm();
    } catch (error: any) {
      console.error('Error creating agent:', error);
      toast.error(error.message || 'Failed to create agent');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      instructions: '',
      model: 'gpt-4o-mini',
      tools: [],
      handoffs: [],
      guardrails: {}, // Changed from [] to {} - should be an object
      model_settings: {
        temperature: 0.7,
        max_tokens: 1500
      }
    });
    setSelectedTools([]);
    setSelectedHandoffs([]);
    setErrors({});
    setSearchTerm('');
    setHandoffSearchTerm('');
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const handleToolToggle = (toolName: string) => {
    setSelectedTools(prev => 
      prev.includes(toolName)
        ? prev.filter(t => t !== toolName)
        : [...prev, toolName]
    );
  };

  const handleHandoffToggle = (agentName: string) => {
    setSelectedHandoffs(prev => 
      prev.includes(agentName)
        ? prev.filter(name => name !== agentName)
        : [...prev, agentName]
    );
  };

  const filteredTools = availableTools.map(category => ({
    ...category,
    tools: category.tools.filter(tool =>
      tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tool.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.tools.length > 0);

  const filteredAgents = availableAgents.filter(agent =>
    agent.name.toLowerCase().includes(handoffSearchTerm.toLowerCase()) ||
    (agent.description && agent.description.toLowerCase().includes(handoffSearchTerm.toLowerCase()))
  );

  const getSelectedToolsInfo = () => {
    const selectedToolsInfo = availableTools
      .flatMap(category => category.tools)
      .filter(tool => selectedTools.includes(tool.name));
    
    return selectedToolsInfo;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCancel} />
        
        {/* Modal panel */}
        <div className="inline-block w-full max-w-4xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg dark:bg-gray-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              Create New Agent
            </h3>
            <button
              onClick={handleCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agent Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="e.g., Code Review Assistant"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Model
                </label>
                <select
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                >
                  <option value="gpt-4o">GPT-4o (Latest)</option>
                  <option value="gpt-4o-mini">GPT-4o Mini (Faster)</option>
                  <option value="gpt-4">GPT-4 (Premium)</option>
                  <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Economy)</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm"
                placeholder="Brief description of what this agent does"
              />
            </div>

            {/* Instructions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Instructions *
              </label>
              <textarea
                rows={4}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm ${
                  errors.instructions ? 'border-red-500' : ''
                }`}
                placeholder="Detailed instructions for how the agent should behave and what it should do..."
              />
              {errors.instructions && <p className="mt-1 text-sm text-red-600">{errors.instructions}</p>}
            </div>

            {/* Model Settings */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Temperature ({formData.model_settings?.temperature || 0.7})
                </label>
                <input
                  type="range"
                  min="0"
                  max="2"
                  step="0.1"
                  value={formData.model_settings?.temperature || 0.7}
                  onChange={(e) => setFormData({
                    ...formData,
                    model_settings: { ...(formData.model_settings || {}), temperature: parseFloat(e.target.value) }
                  })}
                  className="mt-1 block w-full"
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
                  <span>Focused (0)</span>
                  <span>Balanced (1)</span>
                  <span>Creative (2)</span>
                </div>
                {errors.temperature && <p className="mt-1 text-sm text-red-600">{errors.temperature}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max Tokens
                </label>
                <input
                  type="number"
                  min="100"
                  max="4000"
                  value={formData.model_settings?.max_tokens || 1500}
                  onChange={(e) => setFormData({
                    ...formData,
                    model_settings: { ...(formData.model_settings || {}), max_tokens: parseInt(e.target.value) }
                  })}
                  className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white sm:text-sm ${
                    errors.max_tokens ? 'border-red-500' : ''
                  }`}
                />
                {errors.max_tokens && <p className="mt-1 text-sm text-red-600">{errors.max_tokens}</p>}
              </div>
            </div>

            {/* Tool Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tools ({selectedTools.length} selected)
                </label>
                <button
                  type="button"
                  onClick={() => setShowToolSelector(!showToolSelector)}
                  className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                >
                  {showToolSelector ? 'Hide Tools' : 'Select Tools'}
                </button>
              </div>

              {/* Selected Tools Preview */}
              {selectedTools.length > 0 && (
                <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="flex flex-wrap gap-2">
                    {getSelectedToolsInfo().map((tool) => (
                      <span
                        key={tool.name}
                        className="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-800 bg-indigo-100 rounded-full dark:text-indigo-200 dark:bg-indigo-800"
                      >
                        {tool.name}
                        <button
                          type="button"
                          onClick={() => handleToolToggle(tool.name)}
                          className="ml-1 text-indigo-600 hover:text-indigo-800 dark:text-indigo-300"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Tool Selector */}
              {showToolSelector && (
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4 max-h-96 overflow-y-auto">
                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search tools..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  {/* Tool Categories */}
                  {filteredTools.map((category) => (
                    <div key={category.name} className="mb-6 last:mb-0">
                      <div className="flex items-center mb-3">
                        <span className="text-lg mr-2">{category.icon}</span>
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                          {category.name}
                        </h4>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                          ({category.tools.length} tools)
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                        {category.description}
                      </p>
                      
                      <div className="grid grid-cols-1 gap-2">
                        {category.tools.map((tool) => (
                          <label
                            key={tool.name}
                            className="flex items-start p-2 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedTools.includes(tool.name)}
                              onChange={() => handleToolToggle(tool.name)}
                              className="mt-1 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <div className="ml-3 flex-1">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {tool.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {tool.description}
                              </div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  {filteredTools.length === 0 && searchTerm && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No tools found matching "{searchTerm}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Handoff Configuration */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agent Handoffs ({selectedHandoffs.length} configured)
                </label>
                <button
                  type="button"
                  onClick={() => setShowHandoffSelector(!showHandoffSelector)}
                  className="px-3 py-1 text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400"
                >
                  {showHandoffSelector ? 'Hide Handoffs' : 'Configure Handoffs'}
                </button>
              </div>

              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Configure which agents this agent can hand off tasks to during workflow execution.
              </p>

              {/* Selected Handoffs Preview */}
              {selectedHandoffs.length > 0 && (
                <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div className="space-y-2">
                    {selectedHandoffs.map((handoff) => (
                      <div
                        key={handoff}
                        className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded border"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {handoff}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleHandoffToggle(handoff)}
                          className="ml-2 text-red-600 hover:text-red-800 dark:text-red-400"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Handoff Selector */}
              {showHandoffSelector && (
                <div className="border border-gray-200 dark:border-gray-600 rounded-md p-4 max-h-64 overflow-y-auto">
                  {/* Search */}
                  <div className="mb-4">
                    <input
                      type="text"
                      placeholder="Search agents..."
                      value={handoffSearchTerm}
                      onChange={(e) => setHandoffSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>

                  {/* Available Agents */}
                  <div className="space-y-2">
                    {filteredAgents.map((agent) => {
                      const isSelected = selectedHandoffs.includes(agent.name);
                      return (
                        <div
                          key={agent.id}
                          className={`flex items-center justify-between p-2 rounded-md cursor-pointer transition-colors ${
                            isSelected 
                              ? 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700' 
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                          onClick={() => handleHandoffToggle(agent.name)}
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {agent.name}
                            </div>
                            {agent.description && (
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {agent.description}
                              </div>
                            )}
                            <div className="flex items-center mt-1 space-x-2">
                              <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded">
                                {agent.model}
                              </span>
                              {agent.tools && Object.keys(agent.tools).length > 0 && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {Object.keys(agent.tools).length} tools
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="ml-2">
                            {isSelected ? (
                              <div className="w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            ) : (
                              <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {filteredAgents.length === 0 && handoffSearchTerm && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No agents found matching "{handoffSearchTerm}"
                    </div>
                  )}

                  {filteredAgents.length === 0 && !handoffSearchTerm && (
                    <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                      No other agents available for handoffs in this workspace.
                      <br />
                      <span className="text-xs">Create more agents to enable handoff workflows.</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Creating...' : 'Create Agent'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AgentCreationForm; 